/**
 * ColorLegendFooter.tsx — Shared 5-color health palette legend
 *
 * T2 Remediation Wave — Item 4 (2026-06-10)
 * Canon §19 (CANON_VISUALS_LOCKED.md): Every page that displays colored
 * states (score badges, helix bands, tower bands, forecast bands, flowcharts,
 * pipeline DNA, opportunity DNA) carries the 5-color legend footer.
 *
 * Canon §5 (5-Color Health Palette — LOCKED):
 *   Gray   #6B7280 — Not scored / nothing done yet
 *   Red    #DC2626 — Bad
 *   Orange #EA580C — Concern, decay, stalled
 *   Yellow #EAB308 — Caution
 *   Green  #16A34A — Good
 *
 * Usage: drop <ColorLegendFooter /> at the bottom of any page with colored
 * states. No props required. Compact horizontal strip, canonically correct.
 *
 * Bryan K. Short, Founder and CEO, Mi6 Group.
 */

// Canon §5 — 5-color health palette. No Amber. No light variants. No sixth band.
const LEGEND = [
  { color: "#6B7280", label: "Not scored" },
  { color: "#DC2626", label: "Bad" },
  { color: "#EA580C", label: "Concern" },
  { color: "#EAB308", label: "Caution" },
  { color: "#16A34A", label: "Good" },
] as const;

interface ColorLegendFooterProps {
  /** Optional label override for the "Not scored" state */
  notScoredLabel?: string;
  /** Optional extra margin-top (default 24) */
  marginTop?: number;
}

export function ColorLegendFooter({
  notScoredLabel,
  marginTop = 24,
}: ColorLegendFooterProps = {}) {
  const items = notScoredLabel
    ? [{ color: LEGEND[0].color, label: notScoredLabel }, ...LEGEND.slice(1)]
    : LEGEND;

  return (
    <footer
      aria-label="Color legend"
      style={{
        marginTop,
        paddingTop: 16,
        borderTop: "1px solid #E5E7EB",
        display: "flex",
        flexWrap: "wrap",
        alignItems: "center",
        gap: "8px 20px",
      }}
    >
      <span
        style={{
          fontSize: 10,
          fontWeight: 700,
          color: "#9CA3AF",
          letterSpacing: "0.16em",
          textTransform: "uppercase",
          whiteSpace: "nowrap",
        }}
      >
        Color guide
      </span>
      {items.map((item) => (
        <span
          key={item.label}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 6,
            fontSize: 12,
            color: "#374151",
          }}
        >
          <span
            style={{
              display: "inline-block",
              width: 10,
              height: 10,
              borderRadius: "50%",
              background: item.color,
              flexShrink: 0,
            }}
          />
          {item.label}
        </span>
      ))}
    </footer>
  );
}

export default ColorLegendFooter;
